import React, { useState, useEffect } from "react";

const API_BASE_URL = `${window.location.protocol}//${window.location.host}`

// --- SOUS-COMPOSANTS STYLISÉS POUR SIDEBAR ---

function CouponsTable({ data }) {
  const [copiedCode, setCopiedCode] = React.useState(null);
  // État pour stocker les IDs des lignes "vues"
  const [checkedLines, setCheckedLines] = React.useState(new Set());

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const toggleLine = (index) => {
    const newChecked = new Set(checkedLines);
    if (newChecked.has(index)) {
      newChecked.delete(index);
    } else {
      newChecked.add(index);
    }
    setCheckedLines(newChecked);
  };

  return (
    <table className="w-full text-xs text-left border-collapse min-w-full">
      <thead className="sticky top-0 bg-[#1a1a1e] text-blue-500 z-10 shadow-md">
        <tr>
          <th className="px-4 py-4 w-12 border-b border-gray-800">
            {/* Colonne Checkbox */}
          </th>
          <th className="px-6 py-4 font-black tracking-widest uppercase border-b border-gray-800 w-2/5">
            CODES_PROMO
          </th>
          <th className="px-6 py-4 font-black tracking-widest uppercase border-b border-gray-800">
            OFFRE_DÉTAILLÉE
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-800/30">
        {data.length === 0 ? (
          <tr>
            <td
              colSpan={3}
              className="p-10 text-center text-gray-600 font-mono italic text-lg uppercase tracking-tighter"
            >
              NO_DATA_FOUND
            </td>
          </tr>
        ) : (
          data.map((group, index) => {
            const isChecked = checkedLines.has(index);

            return (
              <tr
                key={index}
                className={`transition-all duration-500 ${
                  isChecked
                    ? "bg-black/40 opacity-20 grayscale scale-[0.98] blur-[0.5px]"
                    : "hover:bg-blue-500/5"
                }`}
              >
                {/* 1. CHECKBOX COLUMN */}
                <td
                  className="px-4 py-4 border-b border-gray-800/20 text-center"
                  onClick={() => toggleLine(index)}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleLine(index)}
                    className="w-5 h-5 rounded border-gray-700 bg-[#0a0a0c] text-blue-600 focus:ring-blue-500 cursor-pointer accent-blue-500"
                  />
                </td>

                {/* 2. CODES COLUMN */}
                <td className="px-6 py-4 border-b border-gray-800/20">
                  <div
                    className={`flex flex-wrap gap-2 transition-opacity ${isChecked ? "pointer-events-none" : ""}`}
                  >
                    {group.codes.map((code) => (
                      <button
                        key={code}
                        onClick={() => copyToClipboard(code)}
                        className={`
                          relative px-4 py-2 rounded-lg font-mono text-base font-black tracking-tighter transition-all
                          border flex items-center gap-2 active:scale-95
                          ${
                            copiedCode === code
                              ? "bg-green-500/20 border-green-500 text-green-400"
                              : "bg-blue-600/10 border-blue-500/30 text-blue-400 hover:bg-blue-600 hover:text-white hover:border-blue-400"
                          }
                        `}
                      >
                        {code}
                        {copiedCode === code && (
                          <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-green-600 text-white text-[10px] px-2 py-1 rounded shadow-lg animate-bounce whitespace-nowrap z-20">
                            COPIÉ !
                            <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-green-600"></span>
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </td>

                {/* 3. MESSAGE COLUMN */}
                <td
                  className={`px-6 py-4 border-b border-gray-800/20 font-semibold text-sm leading-snug transition-all ${isChecked ? "line-through text-gray-700" : "text-gray-200"}`}
                >
                  <div className="flex flex-col gap-1">
                    <span>{group.message}</span>
                    {group.others.length > 0 && (
                      <span
                        className={`text-[10px] font-mono uppercase tracking-widest ${isChecked ? "text-gray-800" : "text-blue-500/60"}`}
                      >
                        {group.codes.length} coupons trouvés
                      </span>
                    )}
                    {group.others.map((element) => {
                      return (
                        <p
                          className="text-[9px] text-gray-600"
                          key={element.code}
                        >
                          {element.code} : {element.message}
                        </p>
                      );
                    })}
                  </div>
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
}

function LastSessionCard({ session }) {
  if (!session)
    return (
      <p className="text-[10px] text-gray-600 italic">Aucune session active</p>
    );

  const magasinList = {
    31752: "Angers",
    32286: "Vallet",
    31801: "St Nazaire",
    32145: "Chap Sur Erdre",
    32056: "Angers Patton",
    31885: "St Seb",
    32192: "Sable Olonne",
  };

  const date = session.date
    ? new Date(session.date).toLocaleString("fr-FR", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "—";

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-blue-500 text-xs">🕑</span>
        <h3 className="text-[10px] font-black tracking-widest text-gray-400 uppercase">
          Session Info
        </h3>
      </div>
      <div className="space-y-2 font-mono">
        <div>
          <p className="text-[9px] text-gray-600 uppercase">ID_SESSION</p>
          <p className="text-sm text-blue-400">{session.id ?? "—"}</p>
        </div>
        <div>
          <p className="text-[9px] text-gray-600 uppercase">DATE_TIME</p>
          <p className="text-xs text-gray-300">{date}</p>
        </div>
        <div>
          <p className="text-[9px] text-gray-600 uppercase">MAGASIN</p>
          <p className="text-xs text-gray-300">
            {magasinList[session.id_magasin] ?? "N/A"}
          </p>
        </div>
        {session.method && (
          <span className="inline-block px-2 py-0.5 text-[9px] font-bold rounded bg-blue-500/10 text-blue-500 border border-blue-500/20">
            {session.method}
          </span>
        )}
      </div>
    </div>
  );
}

// --- COMPOSANT PRINCIPAL ---

function PageResult() {
  const [coupons, setCoupons] = useState([]);
  const [lastScrapSession, setLastScrapSession] = useState(null);
  const [sessionId, setSessionId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("Uniques");

  const couponsUnique = coupons.filter((c) => c.is_free_voucher === true);
  const couponsMultiple = coupons.filter((c) => c.is_free_voucher === false);

  const fetchCoupons = async (session) => {
    try {
      setLoading(true);
      setError(null);
      const id = typeof session === "object" ? session.id : session;
      setLastScrapSession(session);
      const response = await fetch(
        `${API_BASE_URL}/api/get_valid_coupons/${id}`,
      );
      const data = await response.json();
      setCoupons(data);
    } catch (err) {
      setError("Erreur récupération coupons.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSession = async (id) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/get_scrap_session/${id}`,
      );
      const data = await response.json();
      setLastScrapSession(data);
    } catch (err) {
      setError("Erreur session.");
    }
  };

  const handleManualFetch = () => {
    fetchSession(sessionId);
    fetchCoupons(sessionId);
  };

  useEffect(() => {
    const fetchLastSession = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/get_last_scrap_session`,
        );
        const data = await response.json();
        fetchCoupons(data);
        setSessionId(data.id);
      } catch (err) {
        setError("Impossible de charger la dernière session.");
      }
    };
    fetchLastSession();
  }, []);

  useEffect(() => {
    // 1. On définit le timer
    const handler = setTimeout(() => {
      if (sessionId) {
        // console.log("Appel API pour ID:", sessionId);
        handleManualFetch(sessionId);
      }
    }, 500);

    // 2. Le return doit être ICI (directement dans useEffect)
    return () => {
      clearTimeout(handler);
    };
  }, [sessionId]);

  const increment = () => setSessionId((prev) => parseInt(prev || 0) + 1);
  const decrement = () =>
    setSessionId((prev) => Math.max(1, parseInt(prev || 0) - 1));

  return (
    // Correction : Ajout de h-full pour que le flex-1 en dessous fonctionne
    <div className="h-full w-full bg-[#0a0a0c] text-gray-300 flex flex-col overflow-hidden">
      {/* 1. Header (shrink-0 pour rester fixe) */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-800 bg-[#0d0d0f] shrink-0">
        <h2 className="text-sm font-black tracking-widest text-white flex items-center gap-2 uppercase">
          <span className="text-blue-500">📊</span> Archive_Coupons
        </h2>
        {loading && (
          <div className="flex items-center gap-2 text-[10px] font-mono text-blue-400 animate-pulse">
            LOADING_LIVE...
          </div>
        )}
      </div>

      {/* 2. Main Content Layout (Lui aussi doit être h-full pour scroller en interne) */}
      <div className="flex flex-1 overflow-hidden p-4 gap-4 h-full">
        {/* SIDEBAR GAUCHE */}
        <div className="w-64 flex flex-col gap-4 shrink-0 overflow-y-auto pr-2 custom-scrollbar">
          <div className="bg-[#111114] p-4 rounded-xl border border-gray-800 shrink-0">
            <p className="text-[9px] text-gray-600 uppercase font-black mb-3 tracking-widest text-center">
              Recherche
            </p>
            <div className="flex items-center mb-2 border border-gray-700 rounded bg-[#0a0a0c] overflow-hidden">
              <button
                onClick={decrement}
                className="px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
              >
                -
              </button>

              <input
                type="number"
                value={sessionId}
                placeholder="ID..."
                onChange={(e) => setSessionId(e.target.value)}
                className="w-full bg-transparent text-xs text-white p-2 outline-none font-mono text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />

              <button
                onClick={increment}
                className="px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
              >
                +
              </button>
            </div>
          </div>

          <div className="bg-[#111114] border border-gray-800 rounded-xl p-4 shrink-0">
            <LastSessionCard session={lastScrapSession} />
          </div>

          <div className="flex flex-col gap-3 shrink-0">
            {/* Conteneur des onglets */}
            <div className="flex flex-col gap-1 bg-[#111114] p-1.5 rounded-xl border border-gray-800">
              <button
                onClick={() => setActiveTab("Uniques")}
                className={`px-4 py-3 text-[10px] font-black tracking-widest rounded transition-all text-left uppercase ${
                  activeTab === "Uniques"
                    ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                    : "text-gray-500 hover:bg-white/5"
                }`}
              >
                UNIQUE
              </button>
              <button
                onClick={() => setActiveTab("Multiples")}
                className={`px-4 py-3 text-[10px] font-black tracking-widest rounded transition-all text-left uppercase ${
                  activeTab === "Multiples"
                    ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                    : "text-gray-500 hover:bg-white/5"
                }`}
              >
                MULTIPLE
              </button>
            </div>

            {/* La petite description en dessous du bloc */}
            {activeTab === "Uniques" && (
              <p className="px-2  text-gray-500 italic leading-tight animate-in fade-in duration-300">
                <span className="text-blue-500 mr-1">ℹ</span>
                Un seul coupon "unique" applicable par commande.
              </p>
            )}
          </div>
        </div>
        {/* COLONNE DROITE (Le Tableau) */}
        <div className="flex-1 h-full bg-[#111114] rounded-xl border border-gray-800 flex flex-col overflow-hidden relative">
          {/* TABLEAU DES UNIQUES */}
          <div
            className={`flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar ${activeTab !== "Uniques" ? "hidden" : ""}`}
          >
            <CouponsTable key="table-uniques" data={couponsUnique} />
          </div>

          {/* TABLEAU DES MULTIPLES */}
          <div
            className={`flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar ${activeTab !== "Multiples" ? "hidden" : ""}`}
          >
            <CouponsTable key="table-multiples" data={couponsMultiple} />
          </div>

          {/* Footer commun */}
          <div className="h-8 bg-[#0d0d0f] border-t border-gray-800 shrink-0 flex items-center px-4 text-[9px] font-mono text-gray-500 italic uppercase">
            ROWS:{" "}
            {(activeTab === "Uniques" ? couponsUnique : couponsMultiple).length}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PageResult;
